import React, { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";

import TableContainer from "Components/Common/TableContainer";
import Flatpickr from "react-flatpickr";

import { getSortObj } from "common/lib/tableDataOptions";
import * as moment from "moment";
// import ContractTypeFilter from "./ContractTypeFilter";
// import AgencyFilter from "./AgencyFilter";
import { normalize } from "common/lib/normalize";
import { toast } from "react-toastify";
// import Import from "./Import";
import { useProfile } from "Components/Hooks/UserHooks";
import { isAllowed } from "common/lib/permissionUtils";
import { getUserActivities } from "common/services/analytics.service";
import PageTypeFilter from "./PageTypeFilter";
import AgencyFilter from "pages/Contracts/AgencyFilter";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  pageType: string;
  agency: string;
  dateRange: DateRange;
}

const AllLogs = ({
  isViewContractors,
  contractorId,
}: {
  isViewContractors?: boolean;
  isAgencyDashboard?: boolean;
  contractorId?: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1, // Starts from 1
    pageSize: Number(searchParams.get("pageSize")) || 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  const [sorted, setSorted] = useState(() => {
    const sortParam = searchParams.get("sort");
    return sortParam
      ? JSON.parse(sortParam)
      : [{ id: "createdAt", desc: true }];
  });

  const [searchValue, setSearchValue] = useState(
    searchParams.get("search") || ""
  );
  const defaultFilterValue: FilterValue = {
    pageType: "",
    agency: "",
    dateRange: {
      fromDate: null,
      toDate: null,
    },
  };
  const [filterValue, setFilterValue] = useState(() => {
    const filterParam = searchParams.get("filter");
    return filterParam ? JSON.parse(filterParam) : defaultFilterValue;
  });

  const [apiParams, setApiParams] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = new URLSearchParams(searchParams);
      params.set("page", String(page));
      params.set("sort", JSON.stringify(sorted));
      params.set("search", searchValue);
      params.set("filter", JSON.stringify(filterValue));
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.agency) {
        andConditions.push({
          user: {
            agency: {
              id: {
                $eq: filterValue.agency,
              },
            },
          },
        });
      }
      if (filterValue?.pageType) {
        andConditions.push({
          frontendPage: {
            id: {
              $eq: filterValue.pageType,
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        const fromDate = new Date(
          filterValue.dateRange.fromDate
        ).toLocaleDateString("en-CA");
        const toDate = new Date(
          filterValue.dateRange.toDate
        ).toLocaleDateString("en-CA");
        andConditions.push({
          createdAt: {
            $between: [fromDate, toDate],
          },
        });
      }

      andConditions.push({
        $or: [
          {
            user: {
              firstName: {
                $containsi: searchValue,
              },
            },
          },
          {
            contractor: {
              contractorName: {
                $containsi: searchValue,
              },
            },
          },
          {
            contract: {
              contractTitle: {
                $containsi: searchValue,
              },
            },
          },
          {
            riskAssessment: {
              reportTitle: {
                $containsi: searchValue,
              },
            },
          },
          {
            pvr: {
              title: {
                $containsi: searchValue,
              },
            },
          },
          {
            capacityAssessment: {
              reportTitle: {
                $containsi: searchValue,
              },
            },
          },
          {
            person: {
              firstName: {
                $containsi: searchValue,
              },
            },
          },
        ],
      });

      try {
        setLoading(true);

        const apiParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "frontendPage",
            "cotnract",
            "contractor",
            "person",
            "capacityAssessment",
            "riskAssessment",
            "pvr",
            "contract",
            "worldBankBannedList",
            "usSanctionsList",
            "ukSanctionsList",
            "unDesignatedList",
            "ungmList",
            "euSanctionsList",
            "user",
            "user.agency",
          ],
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(apiParams);
        const response: any = await getUserActivities(apiParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, searchParams, setSearchParams]
  );

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "user-activity",
        action: "delete",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: "User Name",
        accessorKey: "user.firstName",
        enableColumnFilter: false,
        id: "user.firstName",
        cell: (cell: any) => {
          return (
            `${cell.getValue()} ${cell.row.original?.user?.lastName}` ||
            "Not Provided"
          );
        },
      },
      {
        header: "Agency ",
        accessorKey: "user.agency.name",
        enableColumnFilter: false,
        id: "user.agency.name",
        cell: (cell: any) => {
          return `${cell.getValue()} ` || "Not Provided";
        },
      },
      {
        header: "Page Name",
        accessorKey: "frontendPage.name",
        id: "frontendPage.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },
      {
        header: "Page Content",
        accessorKey: "pageContent",
        id: "pageContent",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <div style={{ whiteSpace: "pre-wrap", width: "300px" }}>
              <Link
                to={`${rowData?.pageUrl}`}
                className="fw-medium link-primary"
              >
                {rowData?.contractor && rowData?.contractor?.contractorName
                  ? rowData?.contractor?.contractorName
                  : rowData?.person && rowData?.person?.name
                  ? rowData?.person?.name
                  : rowData?.contract && rowData?.contract?.contractTitle
                  ? rowData?.contract?.contractTitle
                  : rowData?.lta && rowData?.lta?.contractTitle
                  ? rowData?.lta?.contractTitle
                  : rowData?.usSanctionsList && rowData?.usSanctionsList?.name
                  ? rowData?.usSanctionsList?.name
                  : rowData?.euSanctionsList && rowData?.euSanctionsList?.name
                  ? rowData?.euSanctionsList?.name
                  : rowData?.ungmList && rowData?.ungmList?.name
                  ? rowData?.ungmList?.name
                  : rowData?.unDesignatedList && rowData?.unDesignatedList?.name
                  ? rowData?.unDesignatedList?.name
                  : rowData?.worldBankBannedList &&
                    rowData?.worldBankBannedList?.name
                  ? rowData?.worldBankBannedList?.name
                  : rowData?.ukSanctionsList && rowData?.ukSanctionsList?.name
                  ? rowData?.ukSanctionsList?.name
                  : rowData?.unDesignatedList && rowData?.unDesignatedList?.name
                  ? rowData?.unDesignatedList?.name
                  : `${rowData?.pageUrl}`}
              </Link>
            </div>
          );
        },
      },

      {
        header: "IP Address",
        accessorKey: "ipAddress",
        enableColumnFilter: false,
        id: "ipAddress",
        cell: (cell: any) => {
          return cell.getValue() || "Not Provided";
        },
      },

      {
        header: "Created At",
        accessorKey: "createdAt",
        id: "createdAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null) return null;
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <Row className="g-3">
            <Col xxl={3} lg={6}>
              <div className="search-box">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search activity logs..."
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    setPage(1);
                  }}
                  value={searchValue}
                />
                <i className="ri-search-line search-icon"></i>
              </div>
            </Col>
            <Col xxl={2} lg={6}>
              <PageTypeFilter
                onChange={(value) => {
                  setFilterValue({ ...filterValue, pageType: value });
                  setPage(1);
                }}
                value={filterValue.pageType}
              />
            </Col>
            <Col xxl={3} lg={6}>
              {/* @ts-ignore */}
              <Flatpickr
                className="form-control"
                placeholder="Select Date Range"
                options={{
                  mode: "range",
                  altFormat: "d M, Y",
                  dateFormat: "d M, Y",
                }}
                onChange={(e) => {
                  if (e[0] && e[1]) {
                    setFilterValue({
                      ...filterValue,
                      dateRange: {
                        fromDate: e[0],
                        toDate: e[1],
                      },
                    });
                    setPage(1);
                  }
                }}
                value={[
                  filterValue?.dateRange?.fromDate || "",
                  filterValue?.dateRange?.toDate || "",
                ]}
              />
            </Col>
            <Col xxl={2} lg={6}>
              <AgencyFilter
                onChange={(value) => {
                  setFilterValue({ ...filterValue, agency: value });
                  setPage(1);
                }}
                value={filterValue.agency}
              />
            </Col>
            <Col xxl={2} lg={4}>
              <button
                onClick={() => {
                  setFilterValue(defaultFilterValue);
                  setSearchValue("");
                  setPage(1);
                  setSorted([{ id: "createdAt", desc: true }]);
                }}
                className="btn btn-light material-shadow-none w-100"
              >
                <i className="ri-restart-line align-bottom me-1"></i> Reset
              </button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <Row className="align-items-center g-3">
            <Col md={8}>
              <h5 className="card-title mb-1">Activity Logs</h5>
              <p className="text-muted mb-0">
                A detailed list of all activity logs for the CIMS users.
              </p>
            </Col>
            <div className="col-md-auto ms-auto">
              <div className="d-flex gap-2"></div>
            </div>
          </Row>
        </CardHeader>
        <CardBody>
          <TableContainer
            loading={loading}
            columns={columns}
            data={data || []}
            pagination={pagination}
            setPagination={setPagination}
            sorted={sorted}
            setSorted={setSorted}
            onPageChange={onPageChange}
            FilterSection={() => {}}
            totalRows={meta?.pagination?.total || 0}
            totalPages={meta?.pagination?.pageCount || 0}
            searchValue={searchValue}
            filterValue={filterValue}
            columnVisibility={columnVisibility}
            divClass="table-responsive table-card mb-4"
            tableClass="align-middle table-nowrap mb-0 table-striped"
            theadClass="table-light table-nowrap"
          />
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default AllLogs;
